import React from 'react';
import { Text, TextVariant } from '@naf/text';
import { UserInteractionVariant } from '@naf/user-interaction';
import { Button } from '@naf-simba/elements-buttons';
import { Link } from '@naf-simba/elements-links';
import { GridCol } from '@naf/grid';
import { OilChangeCalculations } from '@appTypes/oilChangeCalculationTypes';
import { oilChangePriceLines } from './oilChangePricing';
import * as S from './OilChangeCalculator.styles';

interface ResultProps {
  calculation: OilChangeCalculations;
  isMember: boolean;
}

const CUSTOMER_SERVICE_PATH = '/kundeservice';

const PriceBreakdown = ({ calculation, isMember }: ResultProps) => {
  const { oilChange } = calculation;

  return (
    <S.Summary>
      {oilChangePriceLines(oilChange, isMember).map((line) => (
        <Text key={line} variant={TextVariant.Header3}>
          {line}
        </Text>
      ))}
      <S.SummaryRow>
        <GridCol s="8" m="8" l="8" xl="8">
          <Text variant={TextVariant.ArticleTextHeader}>{oilChange?.litersOfOil} liter olje</Text>
        </GridCol>
        <GridCol s="4" m="4" l="4" xl="4">
          <Text>{oilChange?.pricePerLiterOfOil},-/liter</Text>
        </GridCol>
      </S.SummaryRow>
      <S.SummaryRow>
        <GridCol s="8" m="8" l="8" xl="8">
          <Text variant={TextVariant.ArticleTextHeader}>Arbeid</Text>
        </GridCol>
        <GridCol s="4" m="4" l="4" xl="4">
          <S.Row>
            {isMember && <Text>{oilChange?.laborPriceForMembers},-</Text>}
            <Text style={{ textDecoration: isMember ? 'line-through' : 'none' }}>
              {oilChange?.laborPriceForNonMembers},-
            </Text>
          </S.Row>
        </GridCol>
      </S.SummaryRow>
      <S.SummaryRow>
        <GridCol s="8" m="8" l="8" xl="8">
          <Text variant={TextVariant.ArticleTextHeader}>Oljefilter</Text>
        </GridCol>
        <GridCol s="4" m="4" l="4" xl="4">
          <Text>{oilChange?.oilFilterPrice},-</Text>
        </GridCol>
      </S.SummaryRow>
    </S.Summary>
  );
};

const CannotCalculate = ({ isElectric }: { isElectric: boolean }) =>
  isElectric ? (
    <>
      <S.StyledUserInteraction variant={UserInteractionVariant.Error}>
        Det ser ikke ut som din bil er aktuell for oljeskift.
      </S.StyledUserInteraction>
      <Text>
        Har det skjedd en feil? Ta kontakt med kundeservice for en prisforespørsel på oljeskift for ditt kjøretøy
      </Text>
      <Link to={CUSTOMER_SERVICE_PATH} text="Kontakt kundeservice" />
    </>
  ) : (
    <>
      <Text variant={TextVariant.Header3}>Vi klarer ikke å kalkulere riktig pris for din bil</Text>
      <Text>Ta kontakt med kundeservice for en prisforespørsel på oljeskift for ditt kjøretøy</Text>
      <Link to={CUSTOMER_SERVICE_PATH} text="Kontakt kundeservice" />
    </>
  );

export const OilChangeResult = ({ calculation, isMember }: ResultProps) => {
  const { vehicle, oilChange, link } = calculation;

  return (
    <S.LookupResultSuccess>
      <S.VehicleText>
        <S.StyledCheckCircle size={22} />
        {vehicle && (
          <S.SuccessText variant={TextVariant.BodyText}>
            {vehicle?.manufactureName} {vehicle?.modelName} {vehicle?.year}
          </S.SuccessText>
        )}
      </S.VehicleText>
      {oilChange && oilChange.litersOfOil && <PriceBreakdown calculation={calculation} isMember={isMember} />}
      {!oilChange?.litersOfOil && vehicle && <CannotCalculate isElectric={vehicle.isElectric} />}
      {link && link.data && (
        <Button href={link.data.href} target={link.data.blank ? '_blank' : '_self'}>
          Finn ledig tid
        </Button>
      )}
    </S.LookupResultSuccess>
  );
};

export const OilChangeLookupError = () => (
  <S.LookupResultError>
    <S.StyledUserInteraction variant={UserInteractionVariant.Error}>
      Vi klarte ikke finne en bil på dette registreringsnummeret.
    </S.StyledUserInteraction>
    <Text>Ta kontakt med kundeservice for en prisforespørsel på oljeskift for ditt kjøretøy.</Text>
    <Link to={CUSTOMER_SERVICE_PATH} text="Kontakt kundeservice" />
  </S.LookupResultError>
);
